import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import API from '../config/api';

const DIAGNOSES_KEY        = (doctorId: string) => `rx_diag_${doctorId}`;
const COMPLAINTS_KEY       = (doctorId: string) => `rx_comp_${doctorId}`;
const MEDICINE_TEMPLATES_KEY = (doctorId: string) => `medicineTemplates_${doctorId}`;

const MAX_ITEMS = 300;

function loadList(key: string): string[] {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveList(key: string, list: string[]) {
  try {
    localStorage.setItem(key, JSON.stringify(list));
  } catch {}
}

function addUnique(existing: string[], value: string): string[] {
  const trimmed = value.trim();
  if (!trimmed) return existing;
  const deduped = existing.filter(s => s.toLowerCase() !== trimmed.toLowerCase());
  return [trimmed, ...deduped].slice(0, MAX_ITEMS);
}

export interface SuggestionOption {
  value: string;
}

export interface HubMedicineEntry {
  name: string;
  contraindications: string[];
}

export interface MedicineTemplateData {
  frequency?: string;
  period?: string;
  timing?: string;
  form?: string;
  route?: string;
  doseQty?: string;
  medicineDetails?: string;
}

export function useDoctorSuggestions(doctorId: string) {
  const id = (doctorId || '').trim();

  const [diagnoses,  setDiagnoses]  = useState<string[]>([]);
  const [complaints, setComplaints] = useState<string[]>([]);

  // API-fetched medicines for this doctor
  const [apiMedicines, setApiMedicines] = useState<string[]>([]);

  // Hub medicines (global catalog) — loaded once, used for autocomplete + contraindications
  const [hubMedicines, setHubMedicines] = useState<HubMedicineEntry[]>([]);

  useEffect(() => {
    if (!id) return;
    setDiagnoses(loadList(DIAGNOSES_KEY(id)));
    setComplaints(loadList(COMPLAINTS_KEY(id)));
  }, [id]);

  // Fetch medicines from API on mount and when doctorId changes
  useEffect(() => {
    if (!id) return;
    axios
      .get(`${API.BASE_URL}${API.ENDPOINTS.DOCTOR_MEDICINES(id)}`)
      .then((res) => {
        if (res.data?.success && Array.isArray(res.data.medicines)) {
          const names: string[] = res.data.medicines.map((m: { name: string }) => m.name);
          setApiMedicines(names);
        }
      })
      .catch(() => {});
  }, [id]);

  // Fetch hub medicines once on mount (shared catalog — independent of doctorId)
  useEffect(() => {
    axios
      .get(`${API.BASE_URL}${API.ENDPOINTS.MEDICINE_HUB_ALL}`)
      .then((res) => {
        if (res.data?.success && Array.isArray(res.data.medicines)) {
          setHubMedicines(res.data.medicines);
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveDiagnosis = useCallback((value: string) => {
    if (!value?.trim() || !id) return;
    const current = loadList(DIAGNOSES_KEY(id));
    const updated = addUnique(current, value);
    saveList(DIAGNOSES_KEY(id), updated);
    setDiagnoses(updated);
  }, [id]);

  const saveComplaint = useCallback((value: string) => {
    if (!value?.trim() || !id) return;
    const current = loadList(COMPLAINTS_KEY(id));
    const updated = addUnique(current, value);
    saveList(COMPLAINTS_KEY(id), updated);
    setComplaints(updated);
  }, [id]);

  // Save a drug name: if it doesn't already exist in the API list, add it silently
  const saveDrug = useCallback((value: string) => {
    const trimmed = value?.trim();
    if (!trimmed || !id) return;
    const alreadyExists = apiMedicines.some(
      (m) => m.toLowerCase() === trimmed.toLowerCase()
    );
    if (alreadyExists) return;
    // Optimistically update local state so autocomplete picks it up immediately
    setApiMedicines((prev) => [...prev, trimmed].sort((a, b) => a.localeCompare(b)));
    // Persist to API in the background (duplicate errors are silently ignored)
    axios
      .post(`${API.BASE_URL}${API.ENDPOINTS.DOCTOR_MEDICINES(id)}`, { name: trimmed })
      .catch(() => {
        // Duplicate or network error — not critical, autocomplete still works
      });
  }, [id, apiMedicines]);

  const filterDiagnoses = useCallback((input: string): SuggestionOption[] => {
    if (!input) return [];
    const lower = input.toLowerCase();
    const startsWith = diagnoses.filter(s => s.toLowerCase().startsWith(lower));
    const contains   = diagnoses.filter(s => !s.toLowerCase().startsWith(lower) && s.toLowerCase().includes(lower));
    return [...startsWith, ...contains].slice(0, 10).map(s => ({ value: s }));
  }, [diagnoses]);

  const filterDrugs = useCallback((input: string): SuggestionOption[] => {
    if (!input) return [];
    const lower = input.toLowerCase();

    // Doctor's own medicines — shown first
    const doctorStartsWith = apiMedicines.filter(s => s.toLowerCase().startsWith(lower));
    const doctorContains   = apiMedicines.filter(s => !s.toLowerCase().startsWith(lower) && s.toLowerCase().includes(lower));
    const doctorNames = new Set(apiMedicines.map(s => s.toLowerCase()));

    // Hub medicines — shown after doctor's list, deduplicated
    const hubStartsWith = hubMedicines
      .filter(h => h.name.toLowerCase().startsWith(lower) && !doctorNames.has(h.name.toLowerCase()))
      .map(h => h.name);
    const hubContains = hubMedicines
      .filter(h => !h.name.toLowerCase().startsWith(lower) && h.name.toLowerCase().includes(lower) && !doctorNames.has(h.name.toLowerCase()))
      .map(h => h.name);

    const merged = [
      ...doctorStartsWith,
      ...doctorContains,
      ...hubStartsWith,
      ...hubContains,
    ];
    return merged.slice(0, 15).map(s => ({ value: s }));
  }, [apiMedicines, hubMedicines]);

  const getContraindications = useCallback((name: string): string[] => {
    if (!name) return [];
    const lower = name.toLowerCase();
    const entry = hubMedicines.find(h => h.name.toLowerCase() === lower);
    return entry?.contraindications ?? [];
  }, [hubMedicines]);

  const filterComplaints = useCallback((input: string): SuggestionOption[] => {
    if (!input) return [];
    const lower = input.toLowerCase();
    const startsWith = complaints.filter(s => s.toLowerCase().startsWith(lower));
    const contains   = complaints.filter(s => !s.toLowerCase().startsWith(lower) && s.toLowerCase().includes(lower));
    return [...startsWith, ...contains].slice(0, 10).map(s => ({ value: s }));
  }, [complaints]);

  const getMedicineTemplate = useCallback((name: string): MedicineTemplateData | null => {
    if (!name || !id) return null;
    try {
      const raw = localStorage.getItem(MEDICINE_TEMPLATES_KEY(id));
      if (!raw) return null;
      const templates: Record<string, MedicineTemplateData> = JSON.parse(raw);
      return templates[name.toLowerCase()] || null;
    } catch {
      return null;
    }
  }, [id]);

  const saveMedicineTemplate = useCallback((name: string, template: MedicineTemplateData) => {
    if (!name || !id) return;
    try {
      const raw = localStorage.getItem(MEDICINE_TEMPLATES_KEY(id));
      const templates: Record<string, MedicineTemplateData> = raw ? JSON.parse(raw) : {};
      // Only store non-empty fields
      const filtered: MedicineTemplateData = {};
      if (template.frequency) filtered.frequency = template.frequency;
      if (template.period)    filtered.period    = template.period;
      if (template.timing)    filtered.timing    = template.timing;
      if (template.form)      filtered.form      = template.form;
      if (template.route)     filtered.route     = template.route;
      if (template.doseQty)   filtered.doseQty   = template.doseQty;
      if (template.medicineDetails) filtered.medicineDetails = template.medicineDetails;
      templates[name.toLowerCase()] = filtered;
      localStorage.setItem(MEDICINE_TEMPLATES_KEY(id), JSON.stringify(templates));
    } catch {}
  }, [id]);

  const deleteMedicineTemplate = useCallback((name: string) => {
    if (!name || !id) return;
    try {
      const raw = localStorage.getItem(MEDICINE_TEMPLATES_KEY(id));
      if (!raw) return;
      const templates: Record<string, MedicineTemplateData> = JSON.parse(raw);
      delete templates[name.toLowerCase()];
      localStorage.setItem(MEDICINE_TEMPLATES_KEY(id), JSON.stringify(templates));
    } catch {}
  }, [id]);

  return {
    diagnoses,
    complaints,
    drugs: apiMedicines,
    saveDiagnosis,
    saveDrug,
    saveComplaint,
    filterDiagnoses,
    filterDrugs,
    filterComplaints,
    getContraindications,
    getMedicineTemplate,
    saveMedicineTemplate,
    deleteMedicineTemplate,
  };
}
